var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Store } from '@incodocs/react-controller';
import result from 'lodash/result';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import isArray from 'lodash/isArray';
export class FormStore extends Store {
    constructor(initialValues, disableValidation) {
        super({
            disableValidation,
            touched: false,
            dirty: false,
            valid: true,
            errors: [],
            values: initialValues || {},
            fields: []
        });
        this.submitListeners = [];
        this.submitFailListeners = [];
        this.subscribeToFailedSubmit = (listener) => {
            this.submitFailListeners.push(listener);
            return () => this.unsubscribeToFailedSubmit(listener);
        };
        this.unsubscribeToFailedSubmit = (listener) => {
            this.submitFailListeners = this.submitFailListeners.filter(ls => ls !== listener);
        };
        this.subscribeToSubmit = (listener) => {
            this.submitListeners.push(listener);
            return () => this.unsubscribeToSubmit(listener);
        };
        this.unsubscribeToSubmit = (listener) => {
            this.submitListeners = this.submitListeners.filter(ls => ls !== listener);
        };
        this.addField = (field) => {
            const defaultValues = {
                validations: [],
                validateOnBlur: false,
                meta: {
                    touched: false,
                    errors: [],
                    dirty: false,
                    validating: false
                }
            };
            const newField = merge(defaultValues, field);
            this.setState(state => (Object.assign(Object.assign({}, state), { fields: [...state.fields, newField] })));
            this.runFieldValidation(newField).then(errors => {
                newField.meta.errors = errors;
                this.setState(state => {
                    return Object.assign(Object.assign({}, state), { valid: newField.meta.errors.length > 0 && state.valid
                            ? false
                            : state.valid, fields: state.fields.map(field => {
                            if (field && field.path === newField.path) {
                                return Object.assign(Object.assign({}, field), { meta: Object.assign(Object.assign({}, field.meta), { errors }) });
                            }
                            return field;
                        }) });
                });
            });
            return newField;
        };
        this.removeField = (path, resetOnUnmount = true) => {
            this.setState(state => {
                const fieldsTokeep = state.fields
                    .map(f => (f && f.path !== path ? f.path : null))
                    .filter(v => v !== null);
                const values = resetOnUnmount
                    ? pick(cloneDeep(state.values), fieldsTokeep)
                    : state.values;
                const fields = [...state.fields.filter(f => f && f.path !== path)];
                return Object.assign(Object.assign({}, state), { values,
                    fields, valid: state.valid
                        ? state.valid
                        : fields.some(field => field.meta.errors.length > 0) });
            });
        };
        this.updateField = (path, updateField) => {
            this.setState(state => {
                let values = state.values;
                if (updateField.path !== undefined && path !== updateField.path) {
                    values = cloneDeep(state.values);
                    const fieldsTokeep = state.fields
                        .map(f => (!!f && f.path !== path ? f.path : null))
                        .filter(v => v !== null);
                    values = pick(values, fieldsTokeep);
                    set(values, updateField.path, result(state.values, path));
                }
                return Object.assign(Object.assign({}, state), { values, fields: state.fields.map(field => {
                        if (field && field.path === path) {
                            return merge({}, field, updateField);
                        }
                        return field;
                    }) });
            });
        };
        this.getField = (path, state = this.state) => {
            return state.fields.find(field => field && field.path === path);
        };
        this.getPreviousField = (path) => {
            if (!this.prevState)
                return;
            return this.prevState.fields.find(field => field && field.path === path);
        };
        this.getFieldIndex = (path, state = this.state) => {
            return state.fields.findIndex(field => field && field.path === path);
        };
        this.getValue = (path, state = this.state) => {
            return result(state.values, path);
        };
        this.changeFieldMeta = (path, meta) => {
            this.setState(state => {
                const field = this.getField(path, state);
                const fieldIndex = this.getFieldIndex(path, state);
                const fields = this.updateFieldMeta(field, fieldIndex, meta, state);
                const newState = Object.assign(Object.assign({}, state), { fields });
                newState.touched = newState.fields.some(field => field.meta.touched);
                return newState;
            });
        };
        /**
         * Protected because it does not save results to state.
         * This is that state can be batched by other functions so as to not cause premature rendering.
         */
        this.updateFieldMeta = (field, fieldIndex, meta, state = this.state) => {
            const fields = [...state.fields];
            const editedField = Object.assign({}, fields[fieldIndex]);
            editedField.meta = Object.assign(Object.assign({}, editedField.meta), meta);
            fields[fieldIndex] = editedField;
            return fields;
        };
        this.change = (path, value) => {
            this.setState(state => {
                const field = this.getField(path, state);
                const dirty = value !== result(this.initialState.values, field.path);
                let meta = Object.assign(Object.assign({}, field.meta), { dirty });
                const fields = this.updateFieldMeta(field, this.getFieldIndex(field.path), meta);
                return Object.assign(Object.assign({}, state), { values: Object.assign(Object.assign({}, state.values), set(cloneDeep(this.state.values), field.path, value)), fields, dirty: fields.some(field => field.meta.dirty) });
            }, state => {
                const field = this.getField(path, state);
                if (!field.validateOnBlur && !this.state.disableValidation) {
                    this.validateField(field.path);
                }
            });
        };
        this.push = (path, value) => {
            let array = this.getValue(path);
            if (array === undefined || array === null)
                array = [];
            if (isArray(array)) {
                this.change(path, [...array, value]);
            }
        };
        this.remove = (path, index) => {
            const array = this.getValue(path);
            if (isArray(array)) {
                this.change(path, [...array.filter((v, vIndex) => index !== vIndex)]);
            }
        };
        /**
         * Protected because it does not save results to state.
         * This is that state can be batched by other functions so as to not cause premature rendering.
         */
        this.runFieldValidation = (field) => {
            const results = field.validations.map(validation => {
                const value = result(this.state.values, field.path);
                return validation(value, field.meta, field.path);
            });
            return Promise.all(results).then(results => {
                return results.filter(result => result !== undefined && result !== null);
            });
        };
        this.validateField = (path) => __awaiter(this, void 0, void 0, function* () {
            const field = this.getField(path);
            const errors = yield this.runFieldValidation(field);
            this.setState(state => {
                const field = this.getField(path, state);
                const fieldIndex = this.getFieldIndex(path, state);
                const fields = this.updateFieldMeta(field, fieldIndex, {
                    errors
                });
                return Object.assign(Object.assign({}, state), { fields, valid: !fields.some(field => field.meta.errors.length > 0) });
            });
        });
        this.validateForm = () => __awaiter(this, void 0, void 0, function* () {
            const fields = yield Promise.all(this.state.fields.map((field) => __awaiter(this, void 0, void 0, function* () {
                const value = result(this.state.values, field.path);
                const errors = [];
                const validationPromises = field.validations.map(validation => {
                    return Promise.resolve(validation(value, field.meta, field.path)).then(result => {
                        if (result) {
                            errors.push(result);
                        }
                    });
                });
                yield Promise.all(validationPromises);
                return Object.assign(Object.assign({}, field), { meta: Object.assign(Object.assign({}, field.meta), { errors }) });
            })));
            const valid = !fields.some(field => field.meta.errors.length > 0);
            this.setState(state => (Object.assign(Object.assign({}, state), { fields, valid })));
            return valid;
        });
        this.submit = (force) => __awaiter(this, void 0, void 0, function* () {
            const valid = yield this.validateForm();
            if (valid || this.state.disableValidation || force) {
                this.setState(state => (Object.assign(Object.assign({}, state), { errors: [] })), () => {
                    this.submitListeners.forEach(listener => {
                        Promise.resolve(listener(this.state)).then(result => {
                            if (result) {
                                this.setState(state => (Object.assign(Object.assign({}, state), { errors: [...state.errors, result] })), state => {
                                    this.submitFailListeners.forEach(failedListener => {
                                        failedListener(state);
                                    });
                                });
                            }
                        });
                    });
                });
            }
        });
    }
}
//# sourceMappingURL=FormStore.js.map